import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import moment from 'moment';
import Progress from '../Util/Progress';
import ErrorDetails from '../Util/ErrorDetails';
import { PortalRunMetadata } from './usePortalRunMetadata';
import { RunMetadata, RunTask } from './useRunPipelineMetadata';
import { isOrchestratorPipeline } from './pipelineVersion';

interface PipelineProps {
  portalRun: PortalRunMetadata | null;
  runMetadata: RunMetadata | null;
  error?: boolean;
}

interface ProgressProps {
  portalRun: PortalRunMetadata | null;
  runMetadata: RunMetadata | null;
  error?: boolean;
}

const Wrapper = styled.div`
  padding: 24px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  gap: 18px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

const Key = styled.div`
  flex-grow: 1;
  width: 30%;
  color: #555;
  font-weight: 500;
`;

const Value = styled.div`
  flex-grow: 1;
  width: 70%;
`;

const EmptyState = styled.div`
  padding: 16px 0;
  color: #888;
  font-style: italic;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
`;

const Badge = styled.span<{ variant?: 'success' | 'error' | 'warning' | 'info' | 'default' }>`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${({ variant }) => {
    switch (variant) {
      case 'success': return '#e6f4ea';
      case 'error': return '#fce8e6';
      case 'warning': return '#fef7e0';
      case 'info': return '#BFDBFE';
      default: return '#eef2ff';
    }
  }};
  color: ${({ variant }) => {
    switch (variant) {
      case 'success': return '#1e7e34';
      case 'error': return '#c5221f';
      case 'warning': return '#b06000';
      case 'info': return '#1D4ED8';
      default: return '#2d3a80';
    }
  }};
`;

const stripesAnim = keyframes`
  from { background-position: 0 0; }
  to { background-position: 28px 0; }
`;

const ProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.85rem;
  color: #555;
`;

type ProgressVariant = 'running' | 'done' | 'failed';

const variantColor = (v: ProgressVariant) => {
  switch (v) {
    case 'done': return '#1e7e34';
    case 'failed': return '#c5221f';
    default: return '#2d3a80';
  }
};

const variantGradient = (v: ProgressVariant) => {
  switch (v) {
    case 'done': return 'linear-gradient(90deg, #34c759 0%, #1e7e34 100%)';
    case 'failed': return 'linear-gradient(90deg, #e86b6b 0%, #c5221f 100%)';
    default: return 'linear-gradient(90deg, #4b5bd6 0%, #2d3a80 100%)';
  }
};

const ProgressPercent = styled.span<{ variant: ProgressVariant }>`
  font-size: 0.95rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${({ variant }) => variantColor(variant)};
`;

const ProgressBarTrack = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
  background: #f1f3f5;
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.06);
`;

const ProgressBarFill = styled.div<{ percent: number; variant: ProgressVariant }>`
  width: ${({ percent }) => percent}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ variant }) => variantGradient(variant)};
  background-size: 28px 100%;
  ${({ variant }) =>
    variant === 'running' &&
    css`
      background-image: linear-gradient(90deg, #4b5bd6 0%, #2d3a80 100%),
        repeating-linear-gradient(
          45deg,
          rgba(255, 255, 255, 0.12) 0px,
          rgba(255, 255, 255, 0.12) 6px,
          transparent 6px,
          transparent 14px
        );
      background-blend-mode: overlay;
      animation: ${stripesAnim} 1.2s linear infinite;
    `}
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Table = styled.table`
  width: 100%;
  margin-top: 24px;
  border-collapse: collapse;
  table-layout: fixed;

  th {
    text-align: left;
    color: #777;
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: uppercase;
    border-bottom: 1px solid #e4e4e4;
    padding: 8px;
  }

  td {
    vertical-align: top;
    border-bottom: 1px solid #eee;
    padding: 8px;
    font-size: 0.95rem;
    word-break: break-word;
  }
`;

const DATETIME_FORMAT = 'MMM Do YYYY, h:mm:ss a';
const TIMEZONE = 'Europe/Amsterdam';

const isSafeHttpsUrl = (url?: string | null): url is string => {
  if (!url) return false;
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
};

const formatStateLabel = (label?: string | null) => (label ? label.replace(/_/g, ' ') : null);

const SUCCESS_STATES = ['SUCCESS', 'SUCCEEDED', 'TERMINATED'];
const ERROR_STATES = ['FAILED', 'INTERNALERROR', 'TIMEDOUT', 'CANCELLED', 'CANCELED'];
const ACTIVE_STATES = ['RUNNING', 'PENDING', 'QUEUED', 'BLOCKED', 'TERMINATING', 'STARTING'];

const getBadgeVariant = (label?: string | null): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (!label) return 'default';
  const key = label.replace(/[^a-z]/gi, '').toUpperCase();
  if (key === 'RUNNING') return 'info';
  if (SUCCESS_STATES.includes(key)) return 'success';
  if (ERROR_STATES.includes(key)) return 'error';
  if (ACTIVE_STATES.includes(key)) return 'warning';
  return 'default';
};

type StateLike = { state?: string; status?: string; stateMessage?: string };

const renderState = (s?: StateLike | null, showMessage = true) => {
  const state = typeof s?.state === 'string' && s.state.trim() ? s.state : undefined;
  const status = typeof s?.status === 'string' && s.status.trim() ? s.status : undefined;
  const stateMessage = typeof s?.stateMessage === 'string' && s.stateMessage.trim() ? s.stateMessage : undefined;
  if (!state && !status && !stateMessage) return '-';
  return (
    <div>
      {(state || status) && (
        <BadgeRow>
          {state && <Badge variant={getBadgeVariant(state)}>{formatStateLabel(state)}</Badge>}
          {status && status !== state && <Badge variant={getBadgeVariant(status)}>{formatStateLabel(status)}</Badge>}
        </BadgeRow>
      )}
      {showMessage && stateMessage && <ErrorDetails message={stateMessage} />}
    </div>
  );
};

const formatTimestamp = (value?: number | null) => {
  if (!isTimestamp(value)) return '-';
  return moment(value).tz(TIMEZONE).format(DATETIME_FORMAT);
};

const isTimestamp = (value?: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const formatDuration = ({ startTime, endTime }: { startTime?: number; endTime?: number }) => {
  if (!isTimestamp(startTime)) return '-';
  const end = isTimestamp(endTime) ? endTime : Date.now();
  const totalSeconds = Math.max(0, Math.round((end - startTime) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds.toString().padStart(2, '0')}s`;
};

const Unavailable: React.FunctionComponent<{ title: string; error?: boolean }> = ({ title, error }) => (
  <Wrapper>
    <h2>{title}</h2>
    {error ? <EmptyState>Run status is not available yet.</EmptyState> : <Progress />}
  </Wrapper>
);

export const RunStatusPipelineV2: React.FunctionComponent<PipelineProps> = ({ portalRun, runMetadata, error }) => {
  if (!runMetadata) return <Unavailable title="Pipeline status" error={error} />;

  const orchestrator = isOrchestratorPipeline(portalRun?.pipelineVersion);
  const platform = orchestrator ? 'Orchestrator' : 'Databricks';
  const clusterName = runMetadata.clusterName || portalRun?.cluster || '-';
  const { jobId, jobRunId, runPageUrl } = runMetadata;
  const { totalTasks = 0, succeededTasks = 0, failedTasks = 0, percent = 0 } = runMetadata.progress ?? {};
  const progressVariant: ProgressVariant = failedTasks > 0
    ? 'failed'
    : totalTasks > 0 && succeededTasks === totalTasks
      ? 'done'
      : 'running';
  const hasRunId = jobRunId !== undefined && jobRunId !== null && jobRunId !== 0;
  const hasJobId = jobId !== undefined && jobId !== null && jobId !== 0;

  return (
    <Wrapper>
      <h2>Pipeline status</h2>
      <Grid>
        <Row>
          <Key>State</Key>
          <Value>{renderState(runMetadata)}</Value>
        </Row>
        <Row>
          <Key>{platform} run</Key>
          <Value>
            {hasRunId ? jobRunId : '-'}
            {isSafeHttpsUrl(runPageUrl) && (
              <div>
                <a href={runPageUrl} target="_blank" rel="noopener noreferrer">
                  Open in {orchestrator ? 'orchestrator' : 'Databricks'}
                </a>
              </div>
            )}
          </Value>
        </Row>
        {!orchestrator && (
          <Row>
            <Key>Databricks job ID</Key>
            <Value>{hasJobId ? jobId : '-'}</Value>
          </Row>
        )}
        {!orchestrator && (
          <Row>
            <Key>Cluster name</Key>
            <Value>{clusterName}</Value>
          </Row>
        )}
        <Row>
          <Key>Started</Key>
          <Value>{formatTimestamp(runMetadata.startTime)}</Value>
        </Row>
        <Row>
          <Key>Finished</Key>
          <Value>{formatTimestamp(runMetadata.endTime)}</Value>
        </Row>
        <Row>
          <Key>Duration</Key>
          <Value>{formatDuration(runMetadata)}</Value>
        </Row>
        <Row>
          <Key>Progress</Key>
          <Value>
            {totalTasks > 0 ? (
              <ProgressWrapper>
                <ProgressHeader>
                  <span>
                    {succeededTasks} of {totalTasks} tasks succeeded
                    {failedTasks > 0 && ` · ${failedTasks} failed`}
                  </span>
                  <ProgressPercent variant={progressVariant}>{percent}%</ProgressPercent>
                </ProgressHeader>
                <ProgressBarTrack>
                  <ProgressBarFill percent={percent} variant={progressVariant} />
                </ProgressBarTrack>
              </ProgressWrapper>
            ) : (
              '-'
            )}
          </Value>
        </Row>
      </Grid>
    </Wrapper>
  );
};

export const RunStatusProgressV2: React.FunctionComponent<ProgressProps> = ({ portalRun, runMetadata, error }) => {
  if (!runMetadata) return <Unavailable title="Pipeline Progression" error={error} />;

  const tasks = (Array.isArray(runMetadata.tasks) ? runMetadata.tasks : []).filter(
    (t): t is RunTask => !!t
  );
  const orchestrator = isOrchestratorPipeline(portalRun?.pipelineVersion);

  return (
    <Wrapper>
      <h2>Pipeline Progression</h2>
      {tasks.length ? (
        <Table>
          <colgroup>
            <col style={{ width: '28%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '14%' }} />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>Step</th>
              <th>State</th>
              <th>Duration</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((task, i) => {
              const idStr = task.id?.toString();
              return (
                <tr key={`${idStr || 'task'}-${i}`}>
                  <td title={idStr}>{task.name || idStr || `Step ${i + 1}`}</td>
                  <td>{renderState(task, false)}</td>
                  <td title={`${formatTimestamp(task.startTime)} - ${formatTimestamp(task.endTime)}`}>
                    {formatDuration(task)}
                  </td>
                  <td>{task.stateMessage ? <ErrorDetails message={task.stateMessage} /> : '-'}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <EmptyState>No {orchestrator ? 'orchestrator steps' : 'Databricks tasks'} have reported yet.</EmptyState>
      )}
    </Wrapper>
  );
};
